import React, { useState, useEffect } from "react";
import { CustomCalendar } from "../../../../components/CustomCalendar/CustomCalendar";
import { useGetUpcomingPublicationScheduleQuery } from "../../../../redux/service/modules/dashboard";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay, parse } from "date-fns";
import "./PublicationSchedule.scss";
import Loader from "../../../../components/common/loader";
import { EventPopup } from "../../../../components/CustomCalendar/EventPopup";

const Legend = () => (
    <div className="calendar-legend">
        <div className="legend-item">
            <span className="legend-dot thematic"></span>
            Thematic
        </div>
        <div className="legend-item">
            <span className="legend-dot ci"></span>
            CI
        </div>
        <div className="legend-item">
            <span className="legend-dot initiation"></span>
            Initiation
        </div>
        <div className="legend-item">
            <span className="legend-dot cis"></span>
            CIS
        </div>
        <div className="legend-item">
            <span className="legend-dot scorecard"></span>
            Scorecard
        </div>
        <div className="legend-item">
            <span className="legend-dot qi"></span>
            QI
        </div>
        <div className="legend-item">
            <span className="legend-dot sector-update"></span>
            Sector Update
        </div>
        <div className="legend-item">
            <span className="legend-dot planned"></span>
            Planned
        </div>
    </div>
);

const PublicationSchedule = ({ setRequiredFilters, appliedFilters }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [currentView, setCurrentView] = useState("month");
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        setRequiredFilters(["sectors", "report_type"]);
    }, [setRequiredFilters]);

    const formatDisplayDate = (dateStr) => {
        const date = parse(dateStr, "dd-MM-yyyy", new Date());
        return format(date, "MMMM d, yyyy");
    };

    const getDateRange = (date, view) => {
        switch (view) {
            case 'month':
                return {
                    startDate: format(startOfMonth(date), "dd-MM-yyyy"),
                    endDate: format(endOfMonth(date), "dd-MM-yyyy")
                };
            case 'week':
                return {
                    startDate: format(startOfWeek(date), "dd-MM-yyyy"),
                    endDate: format(endOfWeek(date), "dd-MM-yyyy")
                };
            case 'day':
                return {
                    startDate: format(startOfDay(date), "dd-MM-yyyy"),
                    endDate: format(endOfDay(date), "dd-MM-yyyy")
                };
            default:
                return {
                    startDate: format(startOfMonth(date), "dd-MM-yyyy"),
                    endDate: format(endOfMonth(date), "dd-MM-yyyy")
                };
        }
    };

    const dateRange = getDateRange(currentDate, currentView);

    const { data: rawData, isLoading } = useGetUpcomingPublicationScheduleQuery(
        {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            appliedFilters: appliedFilters,
        }
    );

    const events = rawData?.map((event) => {
        if (event.type === "publication") {
            return {
                id: event.id,
                title: `${event.publication.report_title}`,
                start: new Date(event.start),
                end: new Date(event.end),
                username: event.username,
                email: event.email,
                role: event.role,
                className: event.title.replace("_", "-"),
                type: event.type,
                sector: event.publication.sectors,
                planned: event.planned_date,
                lag: event.publication.lag,
                report_type: event.publication.report_type,
                publicationGroup: event.title
            };
        } else {
            return {
                id: event.id,
                title: `${event.username} - ${event.title
                    .split("_")
                    .map(
                        (word) =>
                            word.charAt(0).toUpperCase() + word.slice(1)
                    )
                    .join(" ")}`,
                start: new Date(event.start),
                end: new Date(event.end),
                username: event.username,
                email: event.email,
                role: event.role,
                className: event.title.replace("_", "-"),
                type: event.type,
                sector: event.sectors,
                publicationGroup: event.title
            };
        }
    }) || [];

    const handleSelectEvent = (event, e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setPopupPosition({
            x: rect.left,
            y: rect.bottom + window.scrollY,
        });
        setSelectedEvent(event);
    };

    const handleNavigate = (newDate) => {
        setCurrentDate(newDate);
    };

    const handleViewChange = (newView) => {
        setCurrentView(newView);
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="publication-schedule">
            <CustomCalendar
                events={events}
                view={currentView}
                onView={handleViewChange}
                onSelectEvent={handleSelectEvent}
                onNavigate={handleNavigate}
                height="800px"
            />
            <Legend />

            {selectedEvent && (
                <EventPopup
                    event={selectedEvent}
                    onClose={() => setSelectedEvent(null)}
                    position={popupPosition}
                    type={selectedEvent.type}
                />
            )}
        </div>
    );
};

export default PublicationSchedule;
